import { v5 as uuidv5 } from "uuid";

export const UUID_NAMESPACE_EFORT =
  "4f3c8b7e-6a21-4c9d-9f0a-2b6e51b7d341";

const UUID_FORMAT_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const forceUuidV4Format = (uuidValue) => {
  const normalizedUuid =
    typeof uuidValue === "string" ? uuidValue.trim().toLowerCase() : "";
  if (!UUID_FORMAT_REGEX.test(normalizedUuid)) {
    return "";
  }

  const characters = normalizedUuid.split("");
  characters[14] = "4";
  const variantNibble = Number.parseInt(characters[19], 16);
  if (Number.isNaN(variantNibble)) {
    return "";
  }
  characters[19] = ((variantNibble & 0x3) | 0x8).toString(16);

  return characters.join("");
};

const deriveDeterministicUuidV4 = (nameSeed) =>
  forceUuidV4Format(uuidv5(nameSeed, UUID_NAMESPACE_EFORT));

export const INVITATION_STATE = Object.freeze({
  PENDING: "PENDING",
  ACCEPTING: "ACCEPTING",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED",
  CANCELED: "CANCELED",
});

export const INVITATION_PROMOTION_STATUS = Object.freeze({
  NOT_STARTED: "NOT_STARTED",
  RUNNING: "RUNNING",
  DONE: "DONE",
  DONE_EMPTY: "DONE_EMPTY",
  ERROR: "ERROR",
});

export const INVITATION_MAPPING_STATUS = Object.freeze({
  ACTIVE: "ACTIVE",
  SUPERSEDED: "SUPERSEDED",
  CONFLICT: "CONFLICT",
});

export const INVITATION_MAPPING_RESOLUTION_REASON = Object.freeze({
  CANONICAL_ACTIVE_USER: "CANONICAL_ACTIVE_USER",
  SUPERSEDED_BY_CANONICAL_INVITATION: "SUPERSEDED_BY_CANONICAL_INVITATION",
  UNRESOLVED_MULTIPLE_ACTIVE_USERS: "UNRESOLVED_MULTIPLE_ACTIVE_USERS",
});

export const INVITATION_ACTIONABLE_STATES = Object.freeze([
  INVITATION_STATE.PENDING,
  INVITATION_STATE.ACCEPTING,
  INVITATION_STATE.ACCEPTED,
]);

export const INVITATION_TERMINAL_STATES = Object.freeze([
  INVITATION_STATE.REJECTED,
  INVITATION_STATE.CANCELED,
]);

const INVITATION_VALID_STATES = new Set([
  ...INVITATION_ACTIONABLE_STATES,
  ...INVITATION_TERMINAL_STATES,
]);
const INVITATION_ACTIONABLE_STATE_SET = new Set(INVITATION_ACTIONABLE_STATES);
const INVITATION_TERMINAL_STATE_SET = new Set(INVITATION_TERMINAL_STATES);
const INVITATION_BLOCKING_REINVITE_STATE_SET = new Set([
  INVITATION_STATE.PENDING,
  INVITATION_STATE.ACCEPTING,
]);

export const normalizeInvitationEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : "";

export const normalizeInvitationState = (state) => {
  const normalizedState =
    typeof state === "string" ? state.trim().toUpperCase() : "";
  return INVITATION_VALID_STATES.has(normalizedState) ? normalizedState : "";
};

export const isInvitationBlockingReinvite = (state) => {
  const normalizedState = normalizeInvitationState(state);
  return INVITATION_BLOCKING_REINVITE_STATE_SET.has(normalizedState);
};

export const hasBlockingInvitationForReinvite = (invitations = []) => {
  if (!Array.isArray(invitations) || invitations.length === 0) {
    return false;
  }

  return invitations.some((invitation) => {
    const pendingUserId =
      typeof invitation?.pending_user_id === "string"
        ? invitation.pending_user_id.trim()
        : "";

    if (!pendingUserId) {
      // Re-invite blocking is only enforced for new-type invitations.
      return false;
    }

    return isInvitationBlockingReinvite(invitation?.state);
  });
};

export const isInvitationTerminal = (state) =>
  INVITATION_TERMINAL_STATE_SET.has(normalizeInvitationState(state));

export const isInvitationActionableForListener = (state) =>
  INVITATION_ACTIONABLE_STATE_SET.has(normalizeInvitationState(state));

export const buildCoachCancelInvitationPatch = ({
  coachId,
  canceledAt,
  canceledSource = "coach_web",
} = {}) => {
  const normalizedCoachId = String(coachId ?? "").trim();
  const normalizedCanceledSource = String(canceledSource ?? "").trim();
  const patch = {
    state: INVITATION_STATE.CANCELED,
  };

  if (canceledAt !== undefined) {
    patch.canceled_at = canceledAt;
  }

  if (normalizedCoachId) {
    patch.canceled_by_uid = normalizedCoachId;
  }

  if (normalizedCanceledSource) {
    patch.canceled_source = normalizedCanceledSource;
  }

  return patch;
};

export const buildInvitationEmailFields = (email) => ({
  new_user_email: normalizeInvitationEmail(email),
});

export const derivePendingUserId = ({ coachId, email }) => {
  const normalizedEmail = normalizeInvitationEmail(email);
  const normalizedCoachId = String(coachId ?? "").trim();

  if (!normalizedCoachId || !normalizedEmail) {
    return "";
  }

  return deriveDeterministicUuidV4(
    `pending-user|${normalizedCoachId}|${normalizedEmail}`
  );
};

export const derivePromotedBlockId = ({ pendingUserId, pendingBlockId }) => {
  const normalizedPendingUserId = String(pendingUserId ?? "").trim();
  const normalizedPendingBlockId = String(pendingBlockId ?? "").trim();

  if (!normalizedPendingUserId || !normalizedPendingBlockId) {
    return "";
  }

  return deriveDeterministicUuidV4(
    `promoted-block|${normalizedPendingUserId}|${normalizedPendingBlockId}`
  );
};

export const resolveInvitationEmail = (invitation) =>
  invitation?.new_user_email ?? invitation?.new_client_email ?? "";
